import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft, TrendingUp, TrendingDown, DollarSign, ShoppingCart, Package, BarChart3 } from 'lucide-react';
import { useOrderStore, useProductStore, useCategoryStore } from '../../hooks';
import { Card, CardContent } from '@lancar-pos/ui';
import { formatCurrency } from '@lancar-pos/shared';
import { format, subDays, startOfDay, endOfDay, isWithinInterval, eachDayOfInterval } from 'date-fns';
import { id } from 'date-fns/locale';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

type DateFilter = 'today' | 'week' | 'month';

interface StatCardProps {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  trend?: {
    value: number;
    isPositive: boolean;
  };
}

function StatCard({ title, value, subtitle, icon, trend }: StatCardProps) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">{title}</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
            {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
            {trend && (
              <div className={`flex items-center gap-1 mt-2 text-xs ${trend.isPositive ? 'text-green-600' : 'text-red-600'}`}>
                {trend.isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                <span>{trend.value}% dari periode sebelumnya</span>
              </div>
            )}
          </div>
          <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center text-primary-600">
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function DashboardPage() {
  const navigate = useNavigate();
  const { orders } = useOrderStore();
  const { products } = useProductStore();
  const { categories } = useCategoryStore();
  const [dateFilter, setDateFilter] = useState<DateFilter>('today');

  const filteredOrders = useMemo(() => {
    const now = new Date();
    return orders.filter((o) => {
      const orderDate = new Date(o.createdAt);
      switch (dateFilter) {
        case 'today':
          return isWithinInterval(orderDate, { start: startOfDay(now), end: endOfDay(now) });
        case 'week':
          return isWithinInterval(orderDate, { start: startOfDay(subDays(now, 7)), end: endOfDay(now) });
        case 'month':
          return isWithinInterval(orderDate, { start: startOfDay(subDays(now, 30)), end: endOfDay(now) });
        default:
          return true;
      }
    });
  }, [orders, dateFilter]);

  const stats = useMemo(() => {
    const completedOrders = filteredOrders.filter((o) => o.status === 'completed');
    const totalRevenue = completedOrders.reduce((sum, o) => sum + o.total, 0);
    const totalOrders = completedOrders.length;
    const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
    const totalItems = completedOrders.reduce((sum, o) => sum + o.items.reduce((s, i) => s + i.quantity, 0), 0);

    return { totalRevenue, totalOrders, avgOrderValue, totalItems };
  }, [filteredOrders]);

  const topProducts = useMemo(() => {
    const productSales: Record<string, { name: string; quantity: number; revenue: number }> = {};
    
    filteredOrders
      .filter((o) => o.status === 'completed')
      .forEach((order) => {
        order.items.forEach((item) => {
          if (!productSales[item.productId]) {
            productSales[item.productId] = { name: item.productName, quantity: 0, revenue: 0 };
          }
          productSales[item.productId].quantity += item.quantity;
          productSales[item.productId].revenue += item.subtotal;
        });
      });

    return Object.values(productSales)
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);
  }, [filteredOrders]);

  const chartData = useMemo(() => {
    const now = new Date();
    let days: Date[];
    
    switch (dateFilter) {
      case 'today':
        return [{ name: 'Hari Ini', revenue: stats.totalRevenue, orders: stats.totalOrders }];
      case 'week':
        days = eachDayOfInterval({ start: subDays(now, 6), end: now });
        break;
      case 'month':
        days = eachDayOfInterval({ start: subDays(now, 29), end: now });
        break;
      default:
        days = [];
    }

    return days.map((day) => {
      const dayOrders = filteredOrders.filter((o) => {
        const orderDate = new Date(o.createdAt);
        return isWithinInterval(orderDate, { start: startOfDay(day), end: endOfDay(day) });
      });
      const revenue = dayOrders.filter((o) => o.status === 'completed').reduce((sum, o) => sum + o.total, 0);
      return {
        name: format(day, 'dd/MM', { locale: id }),
        revenue,
        orders: dayOrders.filter((o) => o.status === 'completed').length,
      };
    });
  }, [filteredOrders, dateFilter, stats]);

  const categoryData = useMemo(() => {
    const categorySales: Record<string, number> = {};
    
    filteredOrders
      .filter((o) => o.status === 'completed')
      .forEach((order) => {
        order.items.forEach((item) => {
          const product = products.find((p) => p.id === item.productId);
          const catId = product?.categoryId || 'uncategorized';
          categorySales[catId] = (categorySales[catId] || 0) + item.subtotal;
        });
      });

    const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EC4899', '#8B5CF6', '#EF4444'];
    
    return Object.entries(categorySales)
      .map(([catId, value], index) => {
        const category = categories.find((c) => c.id === catId);
        return {
          name: category?.name || 'Tanpa Kategori',
          value,
          color: colors[index % colors.length],
        };
      })
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);
  }, [filteredOrders, products, categories]);

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate({ to: '/app' })}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">Dashboard</h1>
          </div>
          <div className="flex gap-2">
            {(['today', 'week', 'month'] as DateFilter[]).map((filter) => (
              <button
                key={filter}
                onClick={() => setDateFilter(filter)}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                  dateFilter === filter
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {filter === 'today' && 'Hari Ini'}
                {filter === 'week' && 'Minggu Ini'}
                {filter === 'month' && 'Bulan Ini'}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="p-4 lg:p-6 space-y-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total Pendapatan"
            value={formatCurrency(stats.totalRevenue)}
            icon={<DollarSign className="w-6 h-6" />}
          />
          <StatCard
            title="Total Pesanan"
            value={stats.totalOrders.toString()}
            subtitle={`${stats.totalItems} item terjual`}
            icon={<ShoppingCart className="w-6 h-6" />}
          />
          <StatCard
            title="Rata-rata Pesanan"
            value={formatCurrency(stats.avgOrderValue)}
            icon={<BarChart3 className="w-6 h-6" />}
          />
          <StatCard
            title="Produk Aktif"
            value={products.filter((p) => p.isActive).length.toString()}
            subtitle={`${categories.filter((c) => !c._deleted).length} kategori`}
            icon={<Package className="w-6 h-6" />}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <CardContent className="p-4">
              <h3 className="font-semibold text-gray-900 mb-4">Tren Penjualan</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#9CA3AF" />
                    <YAxis tick={{ fontSize: 12 }} stroke="#9CA3AF" tickFormatter={(value) => `Rp ${(value / 1000).toFixed(0)}k`} />
                    <Tooltip
                      formatter={(value: number) => formatCurrency(value)}
                      contentStyle={{ borderRadius: '8px', border: '1px solid #E5E7EB' }}
                    />
                    <Line type="monotone" dataKey="revenue" stroke="#0EA5E9" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <h3 className="font-semibold text-gray-900 mb-4">Penjualan per Kategori</h3>
              <div className="h-64">
                {categoryData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={categoryData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        labelLine={false}
                      >
                        {categoryData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value: number) => formatCurrency(value)} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-gray-400">
                    <p>Belum ada data</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold text-gray-900 mb-4">Produk Terlaris</h3>
            {topProducts.length > 0 ? (
              <div className="space-y-3">
                {topProducts.map((product, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center text-xs font-bold text-primary-600">
                        {index + 1}
                      </span>
                      <div>
                        <p className="font-medium text-gray-900">{product.name}</p>
                        <p className="text-xs text-gray-500">{product.quantity} terjual</p>
                      </div>
                    </div>
                    <span className="font-semibold text-gray-900">{formatCurrency(product.revenue)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <Package className="w-12 h-12 mx-auto mb-2" />
                <p>Belum ada pesanan</p>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
